import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  LinearProgress,
  Menu,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import {
  getInitiativeDetail,
  resetDetailState,
  addMilestone,
  updateMilestone,
  deleteMilestone,
  addComment,
  deleteComment,
  uploadFile,
  deleteFile,
  addLocalComment,
  removeLocalComment,
  addLocalFile,
  removeLocalFile,
  addLocalMilestone,
  updateLocalMilestone,
  removeLocalMilestone,
} from "../../redux/initiatives";
import EditInitiativeModal from "./components/EditInitiativeModal";
import { getAllUsersTeamsListToUseInAllSections } from "../../redux/users/getAllUsersListToUseInAllSections";
import moment from "moment";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const statusColors = {
  future: { bg: "#E3F2FD", color: "#1565C0" },
  assigned: { bg: "#FFF3E0", color: "#E65100" },
  working: { bg: "#E8F5E9", color: "#2E7D32" },
  completed: { bg: "#E0F2F1", color: "#00695C" },
  cancelled: { bg: "#FFEBEE", color: "#C62828" },
};

const priorityColors = {
  low: { bg: "#E8F5E9", color: "#2E7D32" },
  medium: { bg: "#FFF3E0", color: "#E65100" },
  high: { bg: "#FFEBEE", color: "#C62828" },
  critical: { bg: "#F3E5F5", color: "#7B1FA2" },
};

const InitiativeDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { detail } = useSelector((state) => state.Initiatives);
  const { storedListOfUsers } = useSelector((state) => state.users);
  const { adminDetail } = useSelector((state) => state.adminAuth);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newMilestone, setNewMilestone] = useState("");
  const [newMilestoneDueDate, setNewMilestoneDueDate] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [editMilestoneOpen, setEditMilestoneOpen] = useState(false);
  const [editMilestoneName, setEditMilestoneName] = useState("");
  const [editMilestoneDueDate, setEditMilestoneDueDate] = useState(null);

  useEffect(() => {
    dispatch(getInitiativeDetail(id));
    dispatch(getAllUsersTeamsListToUseInAllSections());
    return () => {
      dispatch(resetDetailState());
    };
  }, [dispatch, id]);

  const initiative = detail.data;

  const handleMilestoneToggle = async (milestone) => {
    const result = await dispatch(
      updateMilestone({
        milestoneId: milestone.id,
        data: { is_completed: !milestone.is_completed },
      })
    );
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(updateLocalMilestone(result.payload.data));
    }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone.trim()) return;
    const milestoneData = { name: newMilestone };
    if (newMilestoneDueDate) {
      milestoneData.estimated_completion_date = moment(newMilestoneDueDate).format("YYYY-MM-DD");
    }
    const result = await dispatch(
      addMilestone({
        initiativeId: id,
        data: milestoneData,
      })
    );
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(addLocalMilestone(result.payload.data));
      setNewMilestone("");
      setNewMilestoneDueDate(null);
      toast.success("Milestone added");
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    const result = await dispatch(deleteMilestone(milestoneId));
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(removeLocalMilestone(milestoneId));
      toast.success("Milestone deleted");
    }
    setAnchorEl(null);
  };

  const handleEditMilestoneOpen = () => {
    setEditMilestoneName(selectedMilestone?.name || "");
    setEditMilestoneDueDate(
      selectedMilestone?.estimated_completion_date
        ? require("dayjs")(selectedMilestone.estimated_completion_date)
        : null
    );
    setEditMilestoneOpen(true);
    setAnchorEl(null);
  };

  const handleEditMilestoneSave = async () => {
    if (!editMilestoneName.trim()) return;
    const data = { name: editMilestoneName };
    if (editMilestoneDueDate) {
      data.estimated_completion_date = editMilestoneDueDate.format("YYYY-MM-DD");
    } else {
      data.estimated_completion_date = null;
    }
    const result = await dispatch(
      updateMilestone({
        milestoneId: selectedMilestone.id,
        data,
      })
    );
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(updateLocalMilestone(result.payload.data));
      toast.success("Milestone updated");
    }
    setEditMilestoneOpen(false);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    const result = await dispatch(
      addComment({
        initiativeId: id,
        data: { comment: newComment },
      })
    );
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(addLocalComment(result.payload.data));
      setNewComment("");
    }
  };

  const handleDeleteComment = async (commentId) => {
    const result = await dispatch(deleteComment(commentId));
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(removeLocalComment(commentId));
      toast.success("Comment deleted");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const result = await dispatch(uploadFile({ initiativeId: id, file }));
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(addLocalFile(result.payload.data));
      toast.success("File uploaded");
    } else {
      toast.error("Failed to upload file");
    }
  };

  const handleDeleteFile = async (fileId) => {
    const result = await dispatch(deleteFile(fileId));
    if (result.meta.requestStatus === "fulfilled") {
      dispatch(removeLocalFile(fileId));
      toast.success("File deleted");
    }
  };

  const getMilestoneProgress = () => {
    if (!initiative?.milestones?.length) return 0;
    const completed = initiative.milestones.filter((m) => m.is_completed).length;
    return Math.round((completed / initiative.milestones.length) * 100);
  };

  if (detail.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!initiative) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Initiative not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={() => navigate("/initiatives")} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h4" fontWeight="600">
              {initiative.title}
            </Typography>
            <Chip
              label={initiative.status}
              sx={{
                backgroundColor: statusColors[initiative.status]?.bg,
                color: statusColors[initiative.status]?.color,
                textTransform: "capitalize",
              }}
            />
            <Chip
              label={initiative.priority}
              size="small"
              sx={{
                backgroundColor: priorityColors[initiative.priority]?.bg,
                color: priorityColors[initiative.priority]?.color,
                textTransform: "capitalize",
              }}
            />
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditModalOpen(true)}>
          Edit
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} md={8}>
          {/* Description */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                Description
              </Typography>
              <Typography color="text.secondary">
                {initiative.description || "No description provided"}
              </Typography>
            </CardContent>
          </Card>

          {/* Milestones */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Milestones
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getMilestoneProgress()}% Complete
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getMilestoneProgress()}
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
              />
              <List>
                {initiative.milestones?.map((milestone) => (
                  <ListItem key={milestone.id} disablePadding>
                    <ListItemIcon>
                      <Checkbox
                        checked={milestone.is_completed}
                        onChange={() => handleMilestoneToggle(milestone)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={milestone.name}
                      secondary={
                        milestone.estimated_completion_date
                          ? `Due: ${moment(milestone.estimated_completion_date).format("MMM D, YYYY")}`
                          : null
                      }
                      sx={{
                        textDecoration: milestone.is_completed ? "line-through" : "none",
                        color: milestone.is_completed ? "text.secondary" : "text.primary",
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setSelectedMilestone(milestone);
                          setAnchorEl(e.currentTarget);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  placeholder="Add milestone..."
                  value={newMilestone}
                  onChange={(e) => setNewMilestone(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddMilestone()}
                  sx={{ flex: 1 }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Due Date"
                    value={newMilestoneDueDate}
                    onChange={(newValue) => setNewMilestoneDueDate(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 160 }} />
                    )}
                  />
                </LocalizationProvider>
                <Button variant="contained" onClick={handleAddMilestone}>
                  <AddIcon />
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                Comments
              </Typography>
              <List>
                {initiative.comments?.map((comment) => (
                  <React.Fragment key={comment.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemIcon>
                        <Avatar
                          src={comment.created_by_user?.profile_images?.profile_img_thumbnail}
                          sx={{ width: 32, height: 32 }}
                        >
                          {comment.created_by_user?.first_name?.[0]}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="subtitle2">
                              {comment.created_by_user?.full_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment(comment.createdAt).fromNow()}
                            </Typography>
                          </Box>
                        }
                        secondary={comment.comment}
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))}
              </List>
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                  multiline
                  sx={{ flex: 1 }}
                />
                <IconButton color="primary" onClick={handleAddComment}>
                  <SendIcon />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Details */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                Details
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Owner
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <Avatar
                      src={initiative.owner?.profile_images?.profile_img_thumbnail}
                      sx={{ width: 24, height: 24 }}
                    >
                      {initiative.owner?.first_name?.[0]}
                    </Avatar>
                    <Typography variant="body2">{initiative.owner?.full_name}</Typography>
                  </Box>
                </Box>
                {initiative.secondary_owner && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Secondary Owner
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <Avatar
                        src={initiative.secondary_owner?.profile_images?.profile_img_thumbnail}
                        sx={{ width: 24, height: 24 }}
                      >
                        {initiative.secondary_owner?.first_name?.[0]}
                      </Avatar>
                      <Typography variant="body2">{initiative.secondary_owner?.full_name}</Typography>
                    </Box>
                  </Box>
                )}
                {initiative.objective_completion_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Target Date
                    </Typography>
                    <Typography variant="body2">
                      {moment(initiative.objective_completion_date).format("MMMM D, YYYY")}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {moment(initiative.createdAt).format("MMMM D, YYYY")}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Files */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Files
                </Typography>
                <input
                  type="file"
                  id="file-upload"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />
                <label htmlFor="file-upload">
                  <IconButton component="span" color="primary">
                    <AttachFileIcon />
                  </IconButton>
                </label>
              </Box>
              <List dense>
                {initiative.files?.map((file) => (
                  <ListItem key={file.id}>
                    <ListItemIcon>
                      <FileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.original_name}
                      secondary={moment(file.createdAt).format("MMM D, YYYY")}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        href={file.file_url}
                        target="_blank"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteFile(file.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {!initiative.files?.length && (
                  <Typography variant="body2" color="text.secondary">
                    No files attached
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Milestone Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={handleEditMilestoneOpen}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteMilestone(selectedMilestone?.id)}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Edit Milestone Dialog */}
      <Dialog open={editMilestoneOpen} onClose={() => setEditMilestoneOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Milestone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Milestone Name"
            fullWidth
            value={editMilestoneName}
            onChange={(e) => setEditMilestoneName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date"
              value={editMilestoneDueDate}
              onChange={(newValue) => setEditMilestoneDueDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMilestoneOpen(false)}>Cancel</Button>
          <Button onClick={handleEditMilestoneSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <EditInitiativeModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        initiative={initiative}
        users={(storedListOfUsers?.list || []).filter(
          (user) => user.role?.name === "Admin" || user.role?.name === "Team Leader"
        )}
      />
    </Box>
  );
};

export default InitiativeDetail;
